import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CoreStrengths from './CoreStrengths';
import ProfilePage from './ProfilePage';
import Contact from './Contact';
import Portfolio from './Portfolio';
import Gallery from './Gallery';
import confetti from 'canvas-confetti';

type ShowcaseItem = {
  id: string;
  title: string;
  displayText: string; // Text to show in the big bottom animation
  image: string;
  component?: React.ComponentType;
};

const showcaseItems: ShowcaseItem[] = [
  {
    id: 'whos-this-guy',
    title: 'About',
    displayText: "Who's Chad?",
    image: '/icon-1.png',
    component: ProfilePage
  },
  {
    id: 'my-work',
    title: 'Traits',
    displayText: "My Work",
    image: '/icon-2.png',
    component: CoreStrengths
  },
  {
    id: 'my-portfolio',
    title: 'Dev',
    displayText: "My Apps",
    image: '/icon-3.png',
    component: Portfolio
  },
  {
    id: 'my-gallery',
    title: 'Gallery',
    displayText: "My Work",
    image: '/icon-4.png',
    component: Gallery
  },
  {
    id: 'contact-me',
    title: 'Contact',
    displayText: "Contact",
    image: '/icon-5.png',
    component: Contact
  }
];

// Individual icon glitch effect function
const applyGlitchToElement = (element: HTMLElement) => {
  element.style.animation = 'iconGlitch 0.6s ease-in-out infinite';
  
  if (!document.querySelector('#icon-glitch-styles')) {
    const style = document.createElement('style');
    style.id = 'icon-glitch-styles';
    style.textContent = `
      @keyframes iconGlitch {
        0%, 100% { filter: none; transform: translate(0); }
        10% { filter: hue-rotate(90deg) contrast(150%); transform: translate(-2px, 1px); }
        20% { filter: hue-rotate(180deg) saturate(200%); transform: translate(2px, -1px); }
        30% { filter: invert(1) hue-rotate(270deg); transform: translate(-1px, 2px); }
        40% { filter: contrast(200%) brightness(150%); transform: translate(1px, -2px); }
        50% { filter: blur(1px) hue-rotate(180deg); transform: translate(-2px, -1px); }
        60% { filter: sepia(1) hue-rotate(45deg); transform: translate(2px, 1px); }
        70% { filter: saturate(300%) hue-rotate(90deg); transform: translate(-1px, -2px); }
        80% { filter: contrast(300%) blur(0.5px); transform: translate(1px, 2px); }
        90% { filter: invert(0.8) hue-rotate(270deg); transform: translate(-2px, -1px); }
      }
    `;
    document.head.appendChild(style);
  }
};

const removeGlitchFromElement = (element: HTMLElement) => {
  element.style.animation = '';
};

// Confetti function with support for custom shapes
const triggerConfetti = (x: number, y: number, customShapes?: any[]) => {
  const originX = x / window.innerWidth;
  const originY = y / window.innerHeight;

  const count = 200;
  const defaults = {
    origin: { x: originX, y: originY },
    spread: 360,
    ticks: 50,
    gravity: 0.8,
    decay: 0.94,
    startVelocity: 30,
  };

  // If custom shapes are provided, use them. Otherwise use default shapes
  const shapes = customShapes || ['star', 'circle'];
  const colors = ['#ffffff', '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];

  console.log('Triggering confetti with shapes:', shapes);
  console.log('Custom shapes provided:', customShapes);

  // Single confetti burst
  const confettiConfig = {
    ...defaults,
    particleCount: count,
    scalar: 1.2,
    shapes: shapes,
    colors: colors
  };
  
  console.log('Final confetti config:', confettiConfig);
  confetti(confettiConfig);
};

// Function to create custom SVG shape for confetti (currently unused but available for future use)
// const createCustomConfettiShape = (svgPath: string) => {
//   return confetti.shapeFromPath({ path: svgPath });
// };

// Custom pin shapes for confetti
let customPinShapes: any[] = [];

try {
  // Try with just the path string - simpler approach
  customPinShapes = [
    confetti.shapeFromPath('M10,0 L20,10 L10,20 L0,10 Z'),
    confetti.shapeFromPath('M10,0 L15,10 L10,20 L5,10 Z'),
    confetti.shapeFromPath('M0,0 L10,0 L10,10 L0,10 Z') // Simple square
  ];
  console.log('Custom pin shapes created successfully (v3):', customPinShapes);
} catch (error) {
  console.error('Error creating custom pin shapes:', error);
  customPinShapes = ['star', 'circle']; // Fallback to default shapes
}

const MainShowcase = () => {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<ShowcaseItem | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const iconRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Detect if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is the 'md' breakpoint in Tailwind
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

    const handleItemClick = (item: ShowcaseItem, event: React.MouseEvent) => {
    // Trigger confetti from the click position
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Use custom pin shapes for confetti
    console.log('About to trigger confetti with pins:', customPinShapes);
    triggerConfetti(centerX, centerY, customPinShapes);
    
    // Open the page/modal if it has a component
    setTimeout(() => {
      if (item.component) {
        setActiveItem(item.id);
      }
    }, 100);
    
    console.log('Clicked:', item.title);
  };

  const handleBackToMain = () => {
    setActiveItem(null);
  };

  const handleItemHover = (item: ShowcaseItem) => {
    setHoveredItem(item);
    
    // Only apply glitch effect on desktop (not mobile)
    if (!isMobile) {
      const iconElement = iconRefs.current[item.id];
      if (iconElement) {
        applyGlitchToElement(iconElement);
      }
    }
  };

  const handleItemLeave = () => {
    if (hoveredItem && !isMobile) {
      const iconElement = iconRefs.current[hoveredItem.id];
      if (iconElement) {
        removeGlitchFromElement(iconElement);
      }
    }
    setHoveredItem(null);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Video - Always Present */}
      <video 
        className="fixed top-0 left-0 w-full h-full object-cover z-0"
        autoPlay 
        muted 
        loop
      >
        <source src="/adfed-bg3.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Dark overlay for better text readability */}
      <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-10"></div>
      
      {/* Header - Always Present */}
      <header className="relative z-20 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center">
            {activeItem && (
              <motion.button
                onClick={handleBackToMain}
                className="absolute left-4 text-white hover:text-gray-200 flex items-center px-4 py-2 rounded-lg"
                whileHover={{ scale: 1.05, x: -5 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                ← Back
              </motion.button>
            )}
            
            {/* Logo with subtle "ch" pulse animation */}
            <div className="relative h-12 w-auto">
              {/* Base AdFed logo */}
              <motion.img 
                src="/adfedlogo2_white.png" 
                alt="AdFed Logo" 
                className="h-12 w-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              />
              
              {/* Overlaid chAdFed logo for subtle pulse effect */}
              <motion.img 
                src="/adfedlogo_white.png" 
                alt="chAdFed Logo" 
                className="absolute top-0 left-0 h-12 w-auto"
                animate={{ 
                  opacity: [0, 1, 1, 0] 
                }}
                transition={{ 
                  duration: 5, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  times: [0, 0.4, 0.6, 1]
                }}
              />
            </div>
          </div>
        </div>
      </header>

            {/* Main Content Area */}
      <div className="relative z-20">
        <AnimatePresence mode="wait">
          {!activeItem ? (
            // Main Welcome Area
            <motion.main
              key="welcome"
              className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4 sm:px-6 lg:px-8"
              initial={{ opacity: 1 }}
              exit={{ 
                opacity: 0,
                scale: 0.8,
                transition: { duration: 0.5, ease: "easeInOut" }
              }}
            >
 
            </motion.main>
          ) : (
            // Active Component View
            <motion.main
              key="component"
              className="min-h-[calc(100vh-200px)]"
              initial={{ 
                opacity: 0, 
                scale: 0.9,
                y: 50
              }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: 0
              }}
              exit={{ 
                opacity: 0, 
                scale: 0.9,
                y: 50
              }}
              transition={{ 
                duration: 0.6, 
                delay: 0.3,
                ease: "easeOut" 
              }}
            >
              {showcaseItems.find(item => item.id === activeItem)?.component && (
                <div className="w-full h-full">
                  {React.createElement(showcaseItems.find(item => item.id === activeItem)!.component!)}
                </div>
              )}
            </motion.main>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom Dock Bar - macOS style */}
      <div className="fixed bottom-0 left-0 right-0 z-30 p-2 sm:p-4 lg:p-6">
        <div className="flex justify-center">
          {/* Dock container with glass morphism */}
          <motion.div
            className="backdrop-blur-xl bg-white/10 rounded-2xl px-2 py-2 sm:px-4 sm:py-3 lg:px-8 lg:py-6 border border-white/20 shadow-2xl"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="flex items-end space-x-1 sm:space-x-3 lg:space-x-6">
              {showcaseItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="group cursor-pointer flex flex-col items-center"
                  ref={(el) => { iconRefs.current[item.id] = el; }}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  onClick={(event) => handleItemClick(item, event)}
                  onMouseEnter={() => handleItemHover(item)}
                  onMouseLeave={handleItemLeave}
                >
                  {/* Icon/Image Container */}
                  <motion.div
                    className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 xl:w-20 xl:h-20 relative"
                    whileHover={!isMobile ? { 
                      scale: 1.2,
                      y: -10
                    } : {}}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    {/* Actual icon image */}
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e) => {
                        // Fallback if image doesn't load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    {/* Fallback gradient (hidden by default) */}
                    <div className="hidden w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center shadow-2xl absolute inset-0">
                      <span className="text-white font-bold text-xs sm:text-sm lg:text-lg">
                        {index + 1}
                      </span>
                    </div>
                  </motion.div>
                  
                  {/* Tooltip - appears on hover (desktop only) */}
                  <AnimatePresence>
                    {hoveredItem?.id === item.id && !isMobile && (
                      <motion.div
                        className="absolute bottom-full mb-2 px-2 py-1 bg-black/80 text-white text-xs rounded whitespace-nowrap"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.title}
                        {/* Tooltip arrow */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/80"></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MainShowcase; 